/* "Complete the look" — same-team suggestions in the cart drawer, cart page and product page.
   Markup comes from snippets/cart-upsell-slot.liquid; the suggestions from
   sections/cart-upsell.liquid through the Section Rendering API.

   Tracking, so we can tell whether it sells:
   - Shopify: every added item carries the hidden line property _upsell=<placement>; the cart
     gets the attribute _upsell_used=<placement> (cleared again if no upsold line remains, see
     <cart-upsell-cleanup>). Events also go through Shopify.analytics.publish
     ('pjs_upsell_viewed' / 'pjs_upsell_added').
   - Plausible: "Upsell Viewed" (counted once the block is actually on screen) and
     "Upsell Added", with placement, team and product. */
(function () {
  if (customElements.get('cart-upsell')) return

  const cache = {} // url -> html
  const inflight = {} // url -> Promise<html>, so two blocks on one page share one request
  const viewed = {} // placement|team -> true, one view per page load

  function escapeHtml (text) {
    return String(text).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
    })
  }

  function fetchHtml (url) {
    if (cache[url]) return Promise.resolve(cache[url])
    if (!inflight[url]) {
      /* Resolves to the section HTML, or '' when the response is not the section. */
      const get = function () {
        return fetch(url, { headers: { Accept: 'text/html' } })
          .then(function (response) {
            return response.ok ? response.text() : ''
          })
          .then(function (text) {
            return text.indexOf('data-cd-up-list') !== -1 ? text : ''
          })
          .catch(function () {
            return ''
          })
      }
      /* One retry: Shopify throttles bursts (429), briefly 404s a section while a theme file is
         being published, and can answer a brand-new session with something else. */
      inflight[url] = get()
        .then(function (html) {
          return (
            html ||
            new Promise(function (resolve) {
              setTimeout(resolve, 1200)
            }).then(get)
          )
        })
        .then(function (html) {
          if (html) cache[url] = html
          delete inflight[url]
          return html
        })
        .catch(function () {
          delete inflight[url]
          return ''
        })
    }
    return inflight[url]
  }

  /* A size select starts on the first size in stock, which is usually the smallest (S, 6 7/8).
     Start on the most common size instead; the shopper still sees and can change it. */
  const COMMON_SIZES = [
    '7 3/8',
    '7 1/4',
    '7 1/2',
    'l',
    'm',
    'xl',
    '48',
    '44',
    's/m',
    'm/l',
    'l/xl'
  ]
  /* "M (10/12)", "m(10/12)", "40 / Deep Royal" -> "m", "m", "40". */
  function sizeKey (text) {
    return String(text || '')
      .split(' / ')[0]
      .toLowerCase()
      .replace(/\(.*$/, '')
      .replace(/\s+/g, ' ')
      .trim()
  }
  function findSize (select, key) {
    return Array.prototype.find.call(select.options, function (option) {
      return sizeKey(option.text) === key
    })
  }
  function commonSize (select) {
    if (!select || select.hasAttribute('data-touched')) return
    for (let i = 0; i < COMMON_SIZES.length; i++) {
      const hit = findSize(select, COMMON_SIZES[i])
      if (hit) {
        select.value = hit.value
        return
      }
    }
  }
  function optionPrice (select, fallback) {
    const option = select && select.options[select.selectedIndex]
    const price = option && parseFloat(option.getAttribute('data-price'))
    return isNaN(price) || price == null ? fallback : price
  }
  function money (amount) {
    return '$' + amount.toFixed(2)
  }
  function cartJsUrl () {
    return ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart.js'
  }

  /* Resolves once Plausible has sent the event (or after a short cap), so a page reload
     right after does not cancel it. */
  function track (name, shopifyName, props) {
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
        Shopify.analytics.publish(shopifyName, props)
      }
    } catch (e) {}
    return new Promise(function (resolve) {
      let done = false
      const finish = function () {
        if (!done) {
          done = true
          resolve()
        }
      }
      try {
        if (window.plausible) window.plausible(name, { props, callback: finish })
        else finish()
      } catch (e) {
        finish()
      }
      setTimeout(finish, 1200)
    })
  }

  /* Attribute writes run one after another, so an older "clear" can never land after a newer
     "set". */
  let attrChain = Promise.resolve()
  function postAttribute (value) {
    return fetch(window.routes.cart_update_url + '.js', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _upsell_used: value } })
    })
  }
  function setUpsellAttribute (value) {
    attrChain = attrChain
      .then(function () {
        return postAttribute(value)
      })
      .catch(function () {})
    return attrChain
  }
  function clearUpsellAttributeIfStale () {
    attrChain = attrChain
      .then(function () {
        return fetch(cartJsUrl(), { headers: { Accept: 'application/json' } })
          .then(function (response) {
            return response.json()
          })
          .then(function (cart) {
            const upsold = (cart.items || []).some(function (i) {
              return i.properties && i.properties._upsell
            })
            if (!upsold && cart.attributes && cart.attributes._upsell_used) {
              return postAttribute('')
            }
          })
      })
      .catch(function () {})
    return attrChain
  }

  customElements.define(
    'cart-upsell',
    class CartUpsell extends HTMLElement {
      connectedCallback () {
        this.busy = false
        this.addEventListener('click', (e) => {
          const button = e.target.closest('[data-cd-up-add]')
          if (button) this.add(button)
        })
        if (this.dataset.placement === 'product-page') {
          this.onCartUpdated = () => this.markInCart()
          document.addEventListener('cart:updated', this.onCartUpdated)
        }
        /* Some items cost more in bigger sizes: keep the shown price on the selected size. */
        this.addEventListener('change', (e) => {
          /* Keep our changes to ourselves: on the cart page this block sits inside <cart-items>,
           which treats any change event as a line-quantity edit. */
          e.stopPropagation()
          if (!e.target.matches('select[data-cd-up-variant]')) return
          e.target.setAttribute('data-touched', '')
          const card = e.target.closest('[data-cd-up-product]')
          const price = optionPrice(e.target, parseFloat(card.dataset.cdUpPrice) || 0)
          card.dataset.cdUpPrice = String(price)
          const priceEl = card.querySelector('.cd-up-p')
          if (priceEl) priceEl.textContent = money(price)
        })
        /* In the drawer, wait until it is actually opened: the drawer lives in the layout, so
         loading right away would cost a request (and a false "view") on every page. */
        const drawer = this.closest('cart-drawer')
        if (drawer && !drawer.classList.contains('is-open')) {
          this.waitForOpen = new MutationObserver(() => {
            if (drawer.classList.contains('is-open')) {
              this.waitForOpen.disconnect()
              this.load()
            }
          })
          this.waitForOpen.observe(drawer, { attributes: true, attributeFilter: ['class'] })
        } else if (!drawer && 'IntersectionObserver' in window) {
          /* Product and cart page: fetch when the block comes near the screen. */
          this.near = new IntersectionObserver(
            (entries) => {
              if (
                entries.some(function (entry) {
                  return entry.isIntersecting
                })
              ) {
                this.near.disconnect()
                this.load()
              }
            },
            { rootMargin: '600px 0px' }
          )
          this.near.observe(this.hidden ? this.parentElement || this : this)
        } else {
          this.load()
        }
      }

      disconnectedCallback () {
        if (this.waitForOpen) this.waitForOpen.disconnect()
        if (this.seen) this.seen.disconnect()
        if (this.near) this.near.disconnect()
        if (this.onCartUpdated) document.removeEventListener('cart:updated', this.onCartUpdated)
      }

      markInCart () {
        if (this.dataset.placement !== 'product-page') return
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'
        fetch(root + 'cart.js')
          .then((response) => response.json())
          .then((cart) => {
            const inCart = (cart.items || []).map(function (item) {
              return String(item.product_id)
            })
            this.querySelectorAll('[data-cd-up-product]').forEach(function (card) {
              const button = card.querySelector('[data-cd-up-add]')
              if (!button) return
              const added = inCart.indexOf(card.dataset.cdUpProduct) !== -1
              button.textContent = added ? 'Added' : 'Add'
              button.disabled = added
            })
          })
          .catch(function () {})
      }

      load () {
        let sources = []
        try {
          sources = JSON.parse(this.dataset.sources || '[]')
        } catch (e) {
          sources = []
        }
        if (!sources.length) return
        Promise.all(
          sources.map(function (src) {
            return fetchHtml(src.u).then(function (html) {
              return { src, html }
            })
          })
        ).then((results) => {
          if (this.isConnected) this.paint(results)
        })
      }

      /* Score every candidate from the team's categories and keep the best few.
         Weights follow the v2 plan (docs: "Complete the look v2 — plan"):
           pairing fit 0-30 · audience 0-20 · stock depth 0-20 (+3 high-inventory)
           best-seller rank 0-15 · price vs the anchor -15..+5
         Then at most 2 of one kind, no second hat when the cart already has this team's hat,
       and hide when fewer than `min` survive. Always the same team (the sources are team URLs). */
      paint (results) {
        const exclude = (this.dataset.exclude || '').split(',').filter(Boolean)
        const cartKinds = (this.dataset.cartKinds || '').split(',').filter(Boolean)
        const limit = parseInt(this.dataset.limit, 10) || 3
        const min = parseInt(this.dataset.min, 10) || 2
        const aud = this.dataset.audience || 'adult'
        const anchorPrice = parseFloat(this.dataset.anchorPrice) || 0
        const inCart =
          !this.closest('[data-cd-product-page]') && this.dataset.placement !== 'product-page'
        const best = {}

        results.forEach(function (res) {
          if (!res.html) return
          const holder = document.createElement('template')
          holder.innerHTML = res.html
          holder.content.querySelectorAll('[data-cd-up-product]').forEach(function (card) {
            const id = card.dataset.cdUpProduct
            if (exclude.indexOf(id) !== -1) return
            const kind = card.dataset.kind || 'other'
            if (inCart && kind === 'hat' && cartKinds.indexOf('hat') !== -1) return
            const cAud = card.dataset.aud || 'adult'
            /* Kids', toddler and infant items only on pages of that same size group; women's items
             not on men's pages. Kids' pages may get adult hats, but only adjustable ones. */
            const small = { youth: 1, toddler: 1, infant: 1 }
            if (small[cAud] && cAud !== aud) return
            if ((aud === 'toddler' || aud === 'infant') && cAud !== aud) return
            if (cAud === 'womens' && aud === 'adult') return
            if (aud === 'youth' && kind === 'hat' && card.dataset.fit === '1') return
            let audScore
            if (cAud === aud) audScore = 20
            else if (aud === 'youth' && kind === 'hat') audScore = 14
            else if (aud === 'womens' && kind === 'hat') audScore = 12
            else if (aud === 'womens' && cAud === 'adult') audScore = 4
            else audScore = 10
            const units = parseInt(card.dataset.units, 10) || 0
            const depth =
              Math.min(20, (20 * Math.log10(Math.max(units, 1))) / Math.log10(500)) +
              (card.dataset.hi === '1' ? 3 : 0)
            const rank = parseInt(card.dataset.rank, 10) || 12
            const demand = Math.max(0, 15 - (rank - 1) * 1.25)
            const price = parseFloat(card.dataset.cdUpPrice) || 0
            let priceScore = 0
            if (anchorPrice > 0) {
              priceScore = price <= anchorPrice ? 5 : price <= anchorPrice * 1.25 ? 0 : -15
            }
            const score = (res.src.w || 0) + audScore + depth + demand + priceScore
            if (!best[id] || best[id].score < score) { best[id] = { card, kind, score } }
          })
        })

        const ranked = Object.keys(best)
          .map(function (key) {
            return best[key]
          })
          .sort(function (a, b) {
            return b.score - a.score
          })
        /* The same item in another color reads as a duplicate ("Navy FUSE Tee" + "Orange FUSE
         Tee"): keep only the best-scored color. */
        const colors =
          /\b(navy|orange|white|black|gr[ae]y|heather|charcoal|red|scarlet|maroon|royal|light|powder|blue|green|kelly|gold|cream|pink|purple|silver|teal|brown|natural|camo)\b/g
        const stems = {}
        const picked = []
        const perKind = {}
        ranked.forEach(function (candidate) {
          if (picked.length >= limit) return
          if ((perKind[candidate.kind] || 0) >= 2) return
          const stem = (candidate.card.dataset.cdUpTitle || '')
            .toLowerCase()
            .replace(colors, '')
            .replace(/\s+/g, ' ')
            .trim()
          if (stems[stem]) return
          stems[stem] = true
          perKind[candidate.kind] = (perKind[candidate.kind] || 0) + 1
          picked.push(candidate)
        })
        if (picked.length < min) return

        const list = document.createElement('div')
        list.className = 'cd-up-list'
        list.setAttribute('data-cd-up-list', '')
        picked.forEach(function (candidate, i) {
          candidate.card.dataset.pos = String(i + 1)
          const node = document.importNode(candidate.card, true)
          const sel = node.querySelector('select[data-cd-up-variant]')
          commonSize(sel)
          if (sel && sel.selectedIndex >= 0) {
            const price = optionPrice(sel, parseFloat(node.dataset.cdUpPrice) || 0)
            node.dataset.cdUpPrice = String(price)
            const priceEl = node.querySelector('.cd-up-p')
            if (priceEl && price !== parseFloat(candidate.card.dataset.cdUpPrice)) {
              priceEl.textContent = money(price)
            }
          }
          list.appendChild(node)
        })
        const slot = this.querySelector('[data-cd-up-slot]')
        slot.innerHTML = ''
        /* Bundle: the top pick plus the best pick of a different kind (a hat and a tee beat two tees). */
        let duo = picked.slice(0, 1)
        const other = picked.find(function (candidate) {
          return picked[0] && candidate.kind !== picked[0].kind
        })
        duo.push(other || picked[1])
        duo = duo.filter(Boolean)
        if (this.dataset.layout === 'bundle' && this.renderBundle(slot, duo)) {
          /* bundle drawn */
        } else {
          slot.appendChild(list)
        }
        this.hidden = false
        const onScreen = this.bundle ? duo : picked
        this.shown = onScreen.length
        this.shownKinds = onScreen
          .map(function (candidate) {
            return candidate.kind
          })
          .join(',')
        /* Read by the QA harness (what was picked, whatever the layout). */
        this.picked = onScreen.map(function (candidate) {
          const data = candidate.card.dataset
          return {
            id: data.cdUpProduct,
            title: data.cdUpTitle,
            kind: data.kind,
            aud: data.aud,
            units: +data.units,
            price: +data.cdUpPrice,
            score: Math.round(candidate.score)
          }
        })
        this.watchView()
        this.markInCart()
      }

      /* "Add both": the current product plus the top suggestions, with checkboxes, a live total
         and one button. Uses the size chosen in the product form. Returns false (falls back to the
       list) when the page has no standard product form. */
      renderBundle (slot, picked) {
        const form = document.querySelector(
          'product-form form[action*="/cart/add"], form[data-type="add-to-cart-form"]'
        )
        const idInput = form && form.querySelector('[name="id"]')
        if (!idInput || !picked.length) return false
        const anchorTitle = this.dataset.anchorTitle || 'This item'
        const anchorImg = this.dataset.anchorImage || ''
        const anchorPrice = parseFloat(this.dataset.anchorPrice) || 0

        const wrap = document.createElement('div')
        wrap.className = 'cd-bd'
        let thumbs =
          '<div class="cd-bd-thumbs">' +
          '<span class="cd-bd-th">' +
          (anchorImg
            ? '<img src="' + anchorImg + '" alt="" width="64" height="64" loading="lazy">'
            : '') +
          '</span>'
        picked.forEach(function (candidate) {
          const img = candidate.card.querySelector('.cd-up-ph img')
          thumbs +=
            '<span class="cd-bd-plus" aria-hidden="true">+</span><span class="cd-bd-th">' +
            (img
              ? '<img src="' +
                img.getAttribute('src') +
                '" alt="" width="64" height="64" loading="lazy">'
              : '') +
            '</span>'
        })
        thumbs += '</div>'

        /* Each row: a full-width label (checkbox + name, 44px tall) so tapping the name toggles
         it; the size select sits outside the label so choosing a size does not. */
        let rows =
          '<div class="cd-bd-row"><label class="cd-bd-pick"><input type="checkbox" checked data-bd-anchor>' +
          '<span class="cd-bd-name" title="' +
          escapeHtml(anchorTitle) +
          '"><strong>This item</strong> <span class="cd-bd-sz" data-bd-anchor-size></span></span></label>' +
          '<span class="cd-bd-price" data-bd-price="' +
          anchorPrice +
          '" data-bd-anchor-price>' +
          money(anchorPrice) +
          '</span></div>'
        picked.forEach(function (candidate, i) {
          const card = candidate.card
          const sel = card.querySelector('select[data-cd-up-variant]')
          const hid = card.querySelector('input[data-cd-up-variant]')
          const price = parseFloat(card.dataset.cdUpPrice) || 0
          const title = escapeHtml(card.dataset.cdUpTitle || '')
          rows +=
            '<div class="cd-bd-row cd-bd-add" data-bd-item data-product="' +
            card.dataset.cdUpProduct +
            '" data-kind="' +
            (card.dataset.kind || '') +
            '" data-pos="' +
            (i + 1) +
            '" data-title="' +
            title +
            '" data-base-price="' +
            price +
            '">' +
            '<label class="cd-bd-pick"><input type="checkbox" checked><span class="cd-bd-name">' +
            title +
            '</span></label>' +
            '<span class="cd-bd-price" data-bd-price="' +
            price +
            '">' +
            money(price) +
            '</span>' +
            (sel
              ? '<select class="cd-bd-size" data-bd-variant aria-label="Size for ' +
                title +
                '">' +
                sel.innerHTML +
                '</select>'
              : '<input type="hidden" data-bd-variant value="' + (hid ? hid.value : '') + '">') +
            '</div>'
        })
        /* Shown only while a hat is checked (the discount needs a jersey and a hat); the discount
         itself is applied at checkout, so the total here is before it. */
        let promo = ''
        if (
          this.dataset.promo &&
          this.dataset.kind === 'jersey' &&
          picked.some(function (candidate) {
            return candidate.kind === 'hat'
          })
        ) {
          promo = '<p class="cd-bd-promo" data-bd-promo>' + escapeHtml(this.dataset.promo) + '</p>'
        }
        wrap.innerHTML =
          promo +
          thumbs +
          rows +
          '<div class="cd-bd-total"><span>Total</span><strong data-bd-total></strong></div>' +
          '<p class="cd-bd-msg" data-bd-msg hidden></p>' +
          '<button type="button" class="cd-bd-go" data-bd-go></button>'
        slot.appendChild(wrap)

        /* The product form always has a size selected; show it and use that variant's price. */
        let variants = []
        try {
          variants = JSON.parse(
            (
              document.querySelector(
                'variant-radios script[type="application/json"], variant-selects script[type="application/json"]'
              ) || {}
            ).textContent || '[]'
          )
        } catch (e) {}
        /* form.getAttribute('id'): form.id would return the form's <input name="id"> */
        const formId = form && form.getAttribute('id')
        const qtyInput = formId
          ? document.querySelector('input[name="quantity"][form="' + formId + '"]')
          : null
        const qtyOf = () => Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1)
        const syncAnchor = () => {
          const variant = variants.find(function (item) {
            return String(item.id) === String(idInput.value)
          })
          const qty = qtyOf()
          const sizeEl = wrap.querySelector('[data-bd-anchor-size]')
          const priceEl = wrap.querySelector('[data-bd-anchor-price]')
          if (variant) {
            const size = sizeKey(variant.option1)
            wrap
              .querySelectorAll('select[data-bd-variant]:not([data-touched])')
              .forEach(function (selectEl) {
                const hit = size && findSize(selectEl, size)
                if (hit) selectEl.value = hit.value
                else commonSize(selectEl)
              })
            sizeEl.textContent =
              (variant.title && variant.title !== 'Default Title'
                ? '(' + variant.title + ')'
                : '') + (qty > 1 ? ' × ' + qty : '')
            priceEl.dataset.bdPrice = String((variant.price / 100) * qty)
            priceEl.dataset.bdQty = String(qty)
            priceEl.textContent = money((variant.price / 100) * qty)
          }
        }
        const update = () => {
          syncAnchor()
          wrap.querySelectorAll('[data-bd-item]').forEach(function (row) {
            const selectEl = row.querySelector('select[data-bd-variant]')
            if (!selectEl) return
            const price = optionPrice(selectEl, parseFloat(row.dataset.basePrice) || 0)
            const priceEl = row.querySelector('[data-bd-price]')
            priceEl.dataset.bdPrice = String(price)
            priceEl.textContent = money(price)
          })
          const promoEl = wrap.querySelector('[data-bd-promo]')
          if (promoEl) {
            /* The discount needs the jersey and a hat in the order. */
            promoEl.hidden =
              !wrap.querySelector('[data-bd-anchor]').checked ||
              !Array.prototype.some.call(
                wrap.querySelectorAll('[data-bd-item][data-kind="hat"] input[type="checkbox"]'),
                function (checkbox) {
                  return checkbox.checked
                }
              )
          }
          let total = 0
          let count = 0
          wrap.querySelectorAll('input[type="checkbox"]').forEach(function (checkbox) {
            if (!checkbox.checked) return
            const row = checkbox.closest('.cd-bd-row')
            const priceEl = row.querySelector('[data-bd-price]')
            total += parseFloat(priceEl.dataset.bdPrice) || 0
            count += parseInt(priceEl.dataset.bdQty, 10) || 1
          })
          wrap.querySelector('[data-bd-total]').textContent = money(total)
          const goBtn = wrap.querySelector('[data-bd-go]')
          if (wrap.hasAttribute('data-bd-done')) return
          if (this.busy) {
            goBtn.disabled = true
            return
          }
          goBtn.textContent =
            count > 1
              ? 'Add ' + count + ' to cart'
              : count === 1
                ? 'Add to cart'
                : 'Select an item'
          goBtn.disabled = count === 0
        }
        wrap.addEventListener('change', function (e) {
          if (e.target.matches('select[data-bd-variant]')) {
            e.target.setAttribute('data-touched', '')
          }
          update()
        })
        /* The theme swaps the id input's value after a size change; re-read it a tick later. Same
         for the quantity stepper (its +/- buttons set the value without a change event). */
        document.addEventListener('change', (e) => {
          if (e.target.closest && e.target.closest('variant-radios, variant-selects')) {
            setTimeout(update, 50)
          }
        })
        if (qtyInput) {
          qtyInput.addEventListener('input', update)
          qtyInput.addEventListener('change', update)
          const stepper = qtyInput.closest('quantity-input') || qtyInput.parentElement
          if (stepper) stepper.addEventListener('click', () => setTimeout(update, 50))
        }
        wrap
          .querySelector('[data-bd-go]')
          .addEventListener('click', () => this.addBundle(wrap, idInput, form, update))
        update()
        this.bundle = true
        return true
      }

      addBundle (wrap, idInput, form, update) {
        if (this.busy) return
        const msg = wrap.querySelector('[data-bd-msg]')
        msg.hidden = true
        const anchorBox = wrap.querySelector('[data-bd-anchor]')
        if (anchorBox.checked && !idInput.value) {
          msg.textContent = 'Choose a size above first.'
          msg.hidden = false
          const picker = document.querySelector('variant-radios, variant-selects')
          if (picker) picker.scrollIntoView({ behavior: 'smooth', block: 'center' })
          return
        }
        const added = []
        const addons = []
        wrap.querySelectorAll('[data-bd-item]').forEach(function (row) {
          if (!row.querySelector('input[type="checkbox"]').checked) return
          const variantEl = row.querySelector('[data-bd-variant]')
          if (!variantEl || !variantEl.value) return
          addons.push({
            id: Number(variantEl.value),
            quantity: 1,
            properties: { _upsell: 'product-page-bundle' }
          })
          added.push(row)
        })
        const anchorPriceEl = wrap.querySelector('[data-bd-anchor-price]')
        const qty =
          parseInt(anchorPriceEl && anchorPriceEl.dataset.bdQty, 10) || 1 /* what the row shows */
        const goBtn = wrap.querySelector('[data-bd-go]')
        /* The main Add to Cart stays off while this runs, so the two can't both add the product. */
        const mainAdd = form && form.querySelector('[type="submit"][name="add"]')
        const mainWas = mainAdd ? mainAdd.disabled : false
        const release = () => {
          if (mainAdd) mainAdd.disabled = mainWas
        }
        if (mainAdd) mainAdd.disabled = true
        this.busy = true
        goBtn.disabled = true
        const sections = (this.dataset.sections || '').split(',').filter(Boolean)

        let skippedAnchor = false
        /* If the shopper already added this size with the main button, add only the extras. */
        fetch(cartJsUrl(), { headers: { Accept: 'application/json' } })
          .then(function (response) {
            return response.json()
          })
          .catch(function () {
            return { items: [] }
          })
          .then((cart) => {
            const items = addons.slice()
            const inCart = (cart.items || []).some(function (i) {
              return String(i.variant_id) === String(idInput.value)
            })
            if (anchorBox.checked && inCart) {
              /* Already added with the main button: add only the extras (said once they're in). */
              anchorBox.checked = false
              skippedAnchor = true
              update()
            } else if (anchorBox.checked) {
              items.unshift({ id: Number(idInput.value), quantity: qty })
            }
            if (!items.length) {
              this.busy = false
              release()
              update()
              if (skippedAnchor) {
                msg.textContent = 'This item is already in your cart.'
                msg.hidden = false
              }
              return null
            }
            const body = { items }
            if (sections.length) {
              body.sections = sections.join(',')
              body.sections_url = window.location.pathname
            }
            return fetch(window.routes.cart_add_url + '.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body)
            }).then((response) => response.json())
          })
          .then((state) => {
            if (!state) return
            if (state.status) {
              this.busy = false
              release()
              update()
              msg.textContent =
                state.description || 'One of these just sold out. Please adjust and try again.'
              msg.hidden = false
              return
            }
            /* Done: keep the button off so a second tap cannot add the whole set again. */
            wrap.setAttribute('data-bd-done', '')
            goBtn.textContent = 'Added to cart'
            goBtn.disabled = true
            if (skippedAnchor) {
              msg.textContent = 'This item is already in your cart, so we added the extras.'
              msg.hidden = false
            }
            /* Open the drawer right away (no reload here, so analytics can finish in the background),
             then let the main Add to Cart work again. */
            this.busy = false
            this.dispatchEvent(
              new CustomEvent('cart-upsell:added', { bubbles: true, detail: state })
            )
            release()
            added.forEach((row) =>
              track('Upsell Added', 'pjs_upsell_added', {
                placement: 'product-page-bundle',
                team: this.dataset.team,
                product: row.dataset.title || '',
                price: row.querySelector('[data-bd-price]').dataset.bdPrice || '',
                kind: row.dataset.kind || '',
                position: row.dataset.pos || ''
              })
            )
            if (added.length) setUpsellAttribute('product-page-bundle')
          })
          .catch(() => {
            this.busy = false
            release()
            update()
            msg.textContent = 'Something went wrong. Please try again.'
            msg.hidden = false
          })
      }

      /* Count a view only when the block is on screen, once per placement and team. */
      watchView () {
        const placement = this.bundle ? 'product-page-bundle' : this.dataset.placement
        const key = placement + '|' + this.dataset.team
        if (viewed[key]) return
        const report = () => {
          if (viewed[key]) return
          viewed[key] = true
          track('Upsell Viewed', 'pjs_upsell_viewed', {
            placement,
            team: this.dataset.team,
            anchor: this.dataset.kind + '/' + this.dataset.audience,
            shown: String(this.shown),
            kinds: this.shownKinds || ''
          })
        }
        if (!('IntersectionObserver' in window)) {
          report()
          return
        }
        this.seen = new IntersectionObserver(
          (entries) => {
            if (
              entries.some(function (entry) {
                return entry.isIntersecting
              })
            ) {
              this.seen.disconnect()
              report()
            }
          },
          { threshold: 0.5 }
        )
        this.seen.observe(this)
      }

      add (button) {
        if (this.busy) return
        const card = button.closest('[data-cd-up-product]')
        const field = card && card.querySelector('[data-cd-up-variant]')
        if (!field || !field.value) return
        const placement = this.dataset.placement || 'cart-drawer'
        const team = this.dataset.team
        const sections = (this.dataset.sections || '').split(',').filter(Boolean)
        const body = {
          items: [{ id: Number(field.value), quantity: 1, properties: { _upsell: placement } }]
        }
        if (sections.length) {
          body.sections = sections.join(',')
          body.sections_url = window.location.pathname
        }
        this.busy = true
        button.disabled = true

        const drawer = document.querySelector('cart-drawer')
        const request =
          drawer && drawer.addItem
            ? drawer.addItem(field.value, 1, { _upsell: placement })
            : fetch(window.routes.cart_add_url + '.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body)
            }).then((response) => response.json())
        request
          .then((state) => {
            if (state.status) {
              this.busy = false
              button.disabled = false
              button.textContent = 'Unavailable'
              return
            }
            /* Wait for the order marker and the event before anything re-renders or reloads. */
            return Promise.all([
              setUpsellAttribute(placement),
              track('Upsell Added', 'pjs_upsell_added', {
                placement,
                team,
                product: card.dataset.cdUpTitle || '',
                price: card.dataset.cdUpPrice || '',
                kind: card.dataset.kind || '',
                position: card.dataset.pos || ''
              })
            ]).then(() => {
              this.busy = false
              this.markInCart()
              if (this.isConnected) {
                this.dispatchEvent(
                  new CustomEvent('cart-upsell:added', { bubbles: true, detail: state })
                )
                return
              }
              /* The drawer re-rendered while the request was in flight: update it directly. */
              const drawer = document.querySelector('cart-drawer')
              if (
                drawer &&
                state.sections &&
                state.sections['cart-drawer'] &&
                drawer.renderContents
              ) { drawer.renderContents(state) } else if (document.querySelector('cart-items')) window.location.reload()
            })
          })
          .catch(() => {
            this.busy = false
            button.disabled = false
          })
      }
    }
  )

  /* Rendered by the drawer and cart page when the cart still carries _upsell_used but no line
     has the _upsell property any more (the add-on was removed). Clears the attribute so the
     order is not attributed to "Complete the look". */
  if (!customElements.get('cart-upsell-cleanup')) {
    customElements.define(
      'cart-upsell-cleanup',
      class extends HTMLElement {
        connectedCallback () {
          clearUpsellAttributeIfStale()
        }
      }
    )
  }
})()
